import { config } from './config';

/** The minimum shape every error response conforms to; see `@desktouch/shared`. */
export interface ApiErrorBody {
  error: { code: string; message: string; requestId?: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Factory: the SPA passes in a token getter (from useLogto) so requests are
 *  always authenticated with a fresh access token, refreshed by the SDK. */
export function makeClient(getToken: () => Promise<string | undefined>) {
  const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    const token = await getToken();
    const response = await fetch(`${config.apiBase}${path}`, {
      method,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body ? { 'content-type': 'application/json' } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    // 204 has no body — return undefined without pretending we parsed it.
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const payload = text ? (JSON.parse(text) as T | ApiErrorBody) : (undefined as T);

    if (!response.ok) {
      const err = payload as ApiErrorBody | undefined;
      throw new ApiError(
        response.status,
        err?.error?.code ?? 'unknown',
        err?.error?.message ?? `HTTP ${response.status}`,
        err?.error?.details,
        err?.error?.requestId,
      );
    }
    return payload as T;
  };

  return {
    // --- Read ---------------------------------------------------------------
    searchUsers: (q?: string) =>
      request<{ users: UserListRow[] }>('GET', `/api/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    getUser: (id: string) => request<UserDetail>('GET', `/api/v1/admin/users/${id}`),
    getAudit: (id: string) =>
      request<{ logs: AuditLog[] }>('GET', `/api/v1/admin/users/${id}/audit`),
    getPlans: () => request<{ plans: Plan[] }>('GET', '/api/v1/plans'),
    getMe: () => request<MeResponse>('GET', '/api/v1/me'),
    getMyIdentities: () => request<{ identities: Identity[] }>('GET', '/api/v1/me/identities'),
    updateMe: (body: { displayName?: string; avatarUrl?: string; locale?: string }) =>
      request<MeResponse>('PATCH', '/api/v1/me', body),

    // --- Subscriptions ------------------------------------------------------
    startSubscription: (
      userId: string,
      body: { planKey: string; provider?: string; reason: string; trialDays?: number },
    ) =>
      request<{ id: string; plan: string; status: string; currentPeriodEnd: string | null }>(
        'POST',
        `/api/v1/admin/users/${userId}/subscriptions`,
        body,
      ),
    cancelSubscription: (id: string, body: { atPeriodEnd: boolean; reason: string }) =>
      request<{ id: string; status: string }>('DELETE', `/api/v1/admin/subscriptions/${id}`, body),

    // --- Entitlements -------------------------------------------------------
    grantEntitlement: (
      userId: string,
      body: { featureKey: string; value: boolean | number | string; reason: string; expiresAt?: string },
    ) =>
      request<{ id: string; featureKey: string; value: unknown; source: string; validUntil: string | null }>(
        'POST',
        `/api/v1/admin/users/${userId}/entitlements`,
        body,
      ),
    revokeEntitlement: (id: string, reason: string) =>
      request<{ id: string; revokedAt: string }>('DELETE', `/api/v1/admin/entitlements/${id}`, { reason }),

    // --- Payments & prices ---------------------------------------------------
    listPayments: (query: { status?: string; problem?: string; q?: string; offset?: number } = {}) => {
      const params = new URLSearchParams(
        Object.entries(query).filter(([, value]) => value !== undefined && value !== '').map(([key, value]) => [key, String(value)]),
      );
      return request<{ total: number; payments: AdminPayment[] }>('GET', `/api/v1/admin/payments${params.size ? `?${params}` : ''}`);
    },
    recheckPayment: (id: string, reason: string) =>
      request<{ id: string; status: string }>('POST', `/api/v1/admin/payments/${id}/recheck`, { reason }),
    listPrices: () => request<{ prices: AdminPrice[] }>('GET', '/api/v1/admin/prices'),
    createPrice: (body: { key: string; planKey: string; currency: string; amountMinor: number; intervalMonths: number; sortOrder?: number; reason: string }) =>
      request<AdminPrice>('POST', '/api/v1/admin/prices', body),
    setPriceActive: (id: string, active: boolean, reason: string) =>
      request<AdminPrice>('PATCH', `/api/v1/admin/prices/${id}`, { active, reason }),

    // --- Billing events -----------------------------------------------------
    listBillingEvents: (status?: string) =>
      request<{ total: number; events: BillingEvent[] }>('GET', `/api/v1/admin/billing-events${status ? `?status=${status}` : ''}`),
    replayBillingEvent: (id: string) =>
      request<{ outcome: string; eventId: string; reason?: string }>('POST', `/api/v1/admin/billing-events/${id}/replay`),

    // --- Licences -----------------------------------------------------------
    listLicenses: () => request<{ licenses: License[] }>('GET', '/api/v1/admin/licenses'),
    createLicense: (body: { planKey: string; durationDays?: number; maxRedemptions?: number; expiresAt?: string; note: string }) =>
      request<{ id: string; code: string; plan: string; expiresAt: string | null }>('POST', '/api/v1/admin/licenses', body),
    revokeLicense: (id: string, reason: string) =>
      request<{ id: string; revoked: boolean }>('POST', `/api/v1/admin/licenses/${id}/revoke`, { reason }),

    // --- Lifecycle ----------------------------------------------------------
    suspend: (userId: string, reason: string) =>
      request<{ id: string; status: string }>('POST', `/api/v1/admin/users/${userId}/suspend`, { reason }),
    reinstate: (userId: string, reason: string) =>
      request<{ id: string; status: string }>('POST', `/api/v1/admin/users/${userId}/reinstate`, { reason }),
    deleteUser: (userId: string, reason: string) =>
      request<{ id: string; deletedAt: string }>('DELETE', `/api/v1/admin/users/${userId}`, { reason }),
  };
}

export type ApiClient = ReturnType<typeof makeClient>;

// --- Response types the UI reads ---------------------------------------------
// Kept in sync with apps/api/src/modules/admin/admin.service.ts; the wire types
// intentionally live here rather than being imported from the backend, so the
// UI can be shipped without a monorepo build dependency.

export interface UserListRow {
  id: string;
  email: string | null;
  displayName: string | null;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
  deletedAt: string | null;
}

export interface Device {
  id: string;
  name: string;
  platform: string;
  appVersion: string | null;
  lastSeenAt: string;
  revokedAt: string | null;
}

export interface SubscriptionSummary {
  plan: string;
  status: string;
  provider: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  gracePeriodEndsAt: string | null;
  organizationId: string | null;
}

export interface EntitlementSnapshot {
  plan: string;
  features: Record<string, boolean | number | string>;
}

export interface EntitlementGrant {
  id: string;
  featureKey: string;
  value: unknown;
  source: string;
  reason: string | null;
  createdBy: string | null;
  validFrom: string;
  validUntil: string | null;
  revokedAt: string | null;
}

export interface UserDetail {
  user: {
    id: string;
    logtoUserId: string;
    email: string | null;
    maskedEmail: string | null;
    displayName: string | null;
    status: string;
    statusReason: string | null;
    createdAt: string;
    lastLoginAt: string | null;
    deletedAt: string | null;
  };
  identities: Array<{ provider: string; email: string | null; linkedAt: string }>;
  devices: Device[];
  subscription: SubscriptionSummary;
  entitlements: EntitlementSnapshot;
  grants: EntitlementGrant[];
  payments: UserPayment[];
}

export interface UserPayment {
  id: string;
  status: string;
  providerStatus: string | null;
  provider: string;
  providerOrderId: string | null;
  description: string;
  amountMinor: number;
  currency: string;
  createdAt: string;
  completedAt: string | null;
  periodEnd: string | null;
}

export interface AdminPayment {
  id: string;
  user: { id: string; email: string | null; displayName: string | null } | null;
  plan: string;
  priceKey: string;
  description: string;
  method: string | null;
  provider: string;
  providerOrderId: string | null;
  status: string;
  providerStatus: string | null;
  amountMinor: number;
  currency: string;
  intervalMonths: number;
  createdAt: string;
  completedAt: string | null;
  periodEnd: string | null;
}

export interface AdminPrice {
  id: string;
  key: string;
  plan: string;
  planName: string;
  currency: string;
  amountMinor: number;
  intervalMonths: number;
  active: boolean;
  sortOrder?: number;
  createdAt?: string;
  payments?: number;
}

export interface BillingEvent {
  id: string;
  provider: string;
  providerEventId: string;
  eventType: string;
  processingStatus: string;
  attempts: number;
  receivedAt: string;
  processedAt: string | null;
  error: string | null;
}

export interface License {
  id: string;
  codePrefix: string;
  plan: string;
  durationDays: number | null;
  maxRedemptions: number;
  redemptionCount: number;
  note: string | null;
  createdBy: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface AuditLog {
  id: string;
  actorType: string;
  actorId: string | null;
  action: string;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface Plan {
  key: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  features: Record<string, boolean | number | string>;
}

export interface Identity {
  id: string;
  provider: string;
  email: string | null;
  isPrimary: boolean;
  linkedAt: string;
}

export interface MeResponse {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  locale: string | null;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
  subscription: {
    plan: string;
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  };
}
